import { AssignmentGroupRowView } from '@app/pages/admin/assignment-process-group.view';

/** Flat-feed and group-table columns that accept a header click. */
export type AssignmentSortColumn =
  | 'name'
  | 'state'
  | 'executedOn'
  | 'created'
  | 'progress'
  | 'duration'
  | 'result'
  | 'last';

export type AssignmentSortDirection = 'asc' | 'desc';

export interface AssignmentTableSort {
  column: AssignmentSortColumn;
  dir: AssignmentSortDirection;
}

const RUN_COLUMNS: readonly AssignmentSortColumn[] = [
  'name',
  'state',
  'executedOn',
  'created',
  'progress',
  'duration',
  'result',
];

const GROUP_COLUMNS: readonly AssignmentSortColumn[] = ['name', 'last'];

export function defaultAssignmentSort(mode: 'groups' | 'runs'): AssignmentTableSort {
  return mode === 'runs'
    ? { column: 'state', dir: 'desc' }
    : { column: 'last', dir: 'desc' };
}

/** Unknown columns and directions fall back to the mode default. A missing direction means desc. */
export function parseAssignmentSort(
  mode: 'groups' | 'runs',
  sort: string | null | undefined,
  dir: string | null | undefined,
): AssignmentTableSort {
  const column = sort?.trim() ?? '';
  const allowed = mode === 'runs' ? RUN_COLUMNS : GROUP_COLUMNS;
  const match = allowed.find(candidate => candidate.toLowerCase() === column.toLowerCase());
  if (!match) {
    return defaultAssignmentSort(mode);
  }
  const direction = dir?.trim().toLowerCase() ?? '';
  if (!direction) {
    return { column: match, dir: 'desc' };
  }
  if (direction !== 'asc' && direction !== 'desc') {
    return defaultAssignmentSort(mode);
  }
  return { column: match, dir: direction };
}

/** First click on a column is descending. The next click on the same column flips direction. */
export function toggleAssignmentColumnSort(
  mode: 'groups' | 'runs',
  current: AssignmentTableSort,
  column: AssignmentSortColumn,
): AssignmentTableSort {
  const allowed = mode === 'runs' ? RUN_COLUMNS : GROUP_COLUMNS;
  if (!allowed.includes(column)) {
    return defaultAssignmentSort(mode);
  }
  if (current.column === column) {
    return { column, dir: current.dir === 'desc' ? 'asc' : 'desc' };
  }
  return { column, dir: 'desc' };
}

/** Reorders the full group response. Empty edge dates stay last in both directions. */
export function sortAssignmentGroupRows(
  rows: AssignmentGroupRowView[],
  sort: AssignmentTableSort,
  titleOf: (displayTitle: string) => string,
  locale?: string,
): AssignmentGroupRowView[] {
  const direction = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((left, right) => compareGroupRows(left, right, sort.column, direction, titleOf, locale));
}

function compareGroupRows(
  left: AssignmentGroupRowView,
  right: AssignmentGroupRowView,
  column: AssignmentSortColumn,
  direction: 1 | -1,
  titleOf: (displayTitle: string) => string,
  locale?: string,
): number {
  if (column === 'last') {
    const leftTime = timeOf(left.latest.createdAt);
    const rightTime = timeOf(right.latest.createdAt);
    return compareNullsLast(leftTime, rightTime, direction);
  }
  return titleOf(left.displayTitle).localeCompare(titleOf(right.displayTitle), locale, { sensitivity: 'base' })
    * direction;
}

function compareNullsLast(left: number | null, right: number | null, direction: 1 | -1): number {
  if (left === null && right === null) {
    return 0;
  }
  if (left === null) {
    return 1;
  }
  if (right === null) {
    return -1;
  }
  return (left - right) * direction;
}

function timeOf(value: string): number | null {
  if (!value.trim()) {
    return null;
  }
  const time = Date.parse(value);
  return Number.isNaN(time) ? null : time;
}
